import { useEffect, useState } from 'react';
import Notification from './Notification';
import '../assets/styles/Notification.css';

/**
 * Notifications flottantes, communes à toute l'application.
 *
 * Chaque action qui envoie quelque chose — un devis, un contrat, une
 * convocation, une feuille de présence, un lien d'émargement — doit le dire
 * au même endroit et de la même façon : une petite pastille en haut à
 * droite, qui s'efface d'elle-même. Avant, chaque page faisait à sa manière
 * (un texte gris dans la page, une alerte du navigateur, rien du tout).
 *
 * Le composant <Toasts /> est monté une fois dans App ; n'importe quelle
 * page appelle `toast.succes(...)` ou `toast.erreur(...)` sans rien câbler.
 * Une erreur reste affichée jusqu'à sa fermeture ; un succès part seul.
 */

let file = [];
const abonnes = new Set();
let compteur = 0;

function publier() {
  const copie = [...file];
  for (const f of abonnes) f(copie);
}

export function fermerToast(id) {
  file = file.filter((t) => t.id !== id);
  publier();
}

export function notifierToast({ type = 'succes', titre = null, message, dureeMs }) {
  const texte = String(message || '').trim();
  if (!texte) return null;
  compteur += 1;
  const id = `toast-${compteur}`;
  file = [
    ...file,
    {
      id,
      type,
      titre,
      message: texte,
      dureeMs: dureeMs === undefined ? (type === 'erreur' ? null : 6000) : dureeMs,
    },
  ];
  publier();
  return id;
}

const RE_ECHEC = /impossible|[ée]chou|erreur|refus/i;
const RE_ENVOI = /envoy|relance|\bparti/i;

/** Le ton d'un message libre : ce qui parle d'échec est une erreur. */
export function typeDepuisTexte(texte) {
  return RE_ECHEC.test(String(texte || '')) ? 'erreur' : 'succes';
}

export const toast = {
  succes: (message, titre) =>
    notifierToast({
      type: 'succes',
      titre: titre !== undefined ? titre : RE_ENVOI.test(message) ? 'Envoi effectué' : null,
      message,
    }),
  erreur: (message, titre = 'Action impossible') => notifierToast({ type: 'erreur', titre, message }),
  info: (message, titre = null) => notifierToast({ type: 'info', titre, message }),
  /** Succès ou erreur selon ce que dit le texte. */
  auto: (message) =>
    typeDepuisTexte(message) === 'erreur' ? toast.erreur(message) : toast.succes(message),
};

/** La pile des notifications en cours, à monter une fois dans l'application. */
export function Toasts() {
  const [liste, setListe] = useState(file);

  useEffect(() => {
    abonnes.add(setListe);
    return () => abonnes.delete(setListe);
  }, []);

  if (!liste.length) return null;
  return (
    <div className="notifs-pile">
      {liste.map((t) => (
        <Notification
          key={t.id}
          type={t.type}
          titre={t.titre}
          message={t.message}
          dureeMs={t.dureeMs}
          onFermer={() => fermerToast(t.id)}
        />
      ))}
    </div>
  );
}

/**
 * Pont pour les pages qui gardent un état `flash` (texte) : le texte part
 * en notification flottante dès qu'il est posé, puis l'état est vidé. Rien
 * n'est rendu dans la page.
 */
export function FlashToast({ texte, onFermer }) {
  useEffect(() => {
    if (!texte) return;
    toast.auto(texte);
    if (onFermer) onFermer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [texte]);
  return null;
}

export default Toasts;
